// SCR-STAFF-884 — ثبت هزینه ساده (US-ACC-020؛ AC-ACC-020.1): فهرست هزینه‌ها با وضعیت ثبت، فرم هزینه (دسته، مبلغ، مالیات، تاریخ، شعبه، روش پرداخت
// نقد/بانک/نسیه با طرف آن، شرح و مدرک) و دسته‌های هزینه پویا با نگاشت به حساب (acc.mapping.manage). سند دوطرفه خودکار R-ACC-01 است.
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type AccExpenseCategoryView,
  type AccExpensePaymentMethod,
  type AccExpenseView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  EmptyState,
  ErrorState,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  LoadingState,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  toBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  accKeys,
  Amount,
  EntryStatusChip,
  fileToBase64,
  SCR_STAFF_884,
  saveBlob,
  useAccBranches,
  useBranchName,
} from './common';

const METHODS: readonly AccExpensePaymentMethod[] = ['Cash', 'Bank', 'SupplierCredit'];

/** سقف مدرک (هم‌ارز سرور ۵ مگابایت). */
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;

const expenseSchema = z
  .object({
    categoryId: z.string().min(1, { error: 'common:validation.required' }),
    amount: z
      .number({ error: 'common:validation.required' })
      .int({ error: 'common:validation.money' })
      .positive({ error: 'common:validation.money' })
      .nullable()
      .refine((value) => value !== null, { error: 'common:validation.required' }),
    tax: z
      .number()
      .int({ error: 'common:validation.money' })
      .min(0, { error: 'common:validation.money' })
      .nullable(),
    businessDate: z
      .string()
      .nullable()
      .refine((value) => Boolean(value), { error: 'common:validation.required' }),
    branchId: z.string().min(1, { error: 'common:validation.required' }),
    paymentMethod: z.string(),
    counterpartyId: z.string(),
    description: z.string().max(500),
  })
  .refine((form) => form.paymentMethod === 'Cash' || form.counterpartyId.length > 0, {
    path: ['counterpartyId'],
    error: 'common:validation.required',
  })
  .refine((form) => form.tax === null || form.amount === null || form.tax <= form.amount, {
    path: ['tax'],
    error: 'acc:expense.tax_too_high',
  });

export type ExpenseForm = z.input<typeof expenseSchema>;

/** بدنه API از فرم (منطق خالص؛ تست Vitest). */
export function expenseBody(form: ExpenseForm, document: { fileName: string; contentBase64: string } | null) {
  const counterparty = form.counterpartyId || null;
  return {
    categoryId: form.categoryId,
    amount: { amount: form.amount ?? 0, currency: 'IRR' },
    tax: form.tax ? { amount: form.tax, currency: 'IRR' } : null,
    businessDate: form.businessDate ?? '',
    branchId: form.branchId,
    paymentMethod: form.paymentMethod as AccExpensePaymentMethod,
    cashRegisterId: form.paymentMethod === 'Cash' ? counterparty : null,
    bankAccountId: form.paymentMethod === 'Bank' ? counterparty : null,
    supplierId: form.paymentMethod === 'SupplierCredit' ? counterparty : null,
    description: form.description.trim() || null,
    document,
  };
}

export function ExpensesPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const branches = useAccBranches();
  const branchName = useBranchName();
  const full = access.accessMode === 'Full';
  const canCreate = full && access.can('acc.expense.create');
  const canCategories = full && access.can('acc.mapping.manage');
  const [branchId, setBranchId] = useState('');
  const [adding, setAdding] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const expenses = useInfiniteQuery({
    queryKey: accKeys.expenses(session?.tenantId, branchId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/expenses', {
          params: { query: { branchId: branchId || undefined, cursor: pageParam, limit: 50 } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const download = async (row: AccExpenseView) => {
    const response = await tenantApi.GET('/api/v1/accounting/expenses/{id}/document', {
      params: { path: { id: row.id } },
      parseAs: 'blob',
    });
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `${row.number}`);
    }
  };
  const columns: DataTableColumn<AccExpenseView>[] = [
    { id: 'number', headerKey: 'acc:expense.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'date', headerKey: 'acc:common.date', cell: (row) => <JalaliDate value={row.businessDate} /> },
    { id: 'category', headerKey: 'acc:expense.category', cell: (row) => row.categoryTitle },
    { id: 'branch', headerKey: 'acc:common.branch', cell: (row) => branchName(row.branchId) },
    {
      id: 'method',
      headerKey: 'acc:expense.method',
      cell: (row) => t(`acc:expense_method.${row.paymentMethod}`),
    },
    { id: 'amount', headerKey: 'acc:common.amount', cell: (row) => <Amount money={row.amount} /> },
    { id: 'tax', headerKey: 'acc:expense.tax', cell: (row) => <Amount money={row.tax} /> },
    {
      id: 'total',
      headerKey: 'acc:common.total',
      cell: (row) => <Amount money={row.total} testId="acc-expense-total" />,
    },
    {
      id: 'posting',
      headerKey: 'acc:expense.posting',
      cell: (row) => (row.postingStatus ? <EntryStatusChip status={row.postingStatus} /> : '—'),
    },
    {
      id: 'document',
      headerKey: 'acc:expense.document',
      cell: (row) =>
        row.documentId ? (
          <Button size="small" onClick={() => void download(row)} data-testid="acc-expense-document">
            {t('acc:expense.download_document')}
          </Button>
        ) : (
          '—'
        ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_884}
      titleKey="acc:expense.title"
      subtitleKey="acc:expense.subtitle"
      actions={
        <Stack direction="row" spacing={1}>
          {canCategories ? (
            <Button
              variant="outlined"
              onClick={() => setCategoriesOpen(true)}
              data-testid="acc-expense-categories"
            >
              {t('acc:expense.categories')}
            </Button>
          ) : null}
          {canCreate ? (
            <Button variant="contained" onClick={() => setAdding(true)} data-testid="acc-expense-add">
              {t('acc:expense.add')}
            </Button>
          ) : null}
        </Stack>
      }
      filters={
        <TextField
          select
          size="small"
          label={t('acc:common.branch')}
          value={branchId}
          onChange={(event) => setBranchId(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('acc:common.all')}</MenuItem>
          {branches.map((branch) => (
            <MenuItem key={branch.branchId} value={branch.branchId}>
              {branch.name}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <DataTable
        captionKey="acc:expense.title"
        columns={columns}
        rows={expenses.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={expenses.isPending}
        error={expenses.error}
        onRetry={() => void expenses.refetch()}
        hasMore={expenses.hasNextPage}
        onLoadMore={() => void expenses.fetchNextPage()}
        isFetchingMore={expenses.isFetchingNextPage}
        emptyTitleKey="acc:expense.empty"
      />
      {adding ? <ExpenseDialog onClose={() => setAdding(false)} /> : null}
      {categoriesOpen ? <CategoriesDialog onClose={() => setCategoriesOpen(false)} /> : null}
    </ListPage>
  );
}

/** فرم ثبت هزینه (AC-ACC-020.1). */
function ExpenseDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const branches = useAccBranches();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const options = useQuery({
    queryKey: accKeys.expenseOptions(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/expense-options')),
  });
  const { control, handleSubmit, formState, setError, setValue } = useForm<ExpenseForm>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      categoryId: '',
      amount: null,
      tax: null,
      businessDate: toBusinessDate(new Date()),
      branchId: access.activeBranchId ?? branches[0]?.branchId ?? '',
      paymentMethod: 'Cash',
      counterpartyId: '',
      description: '',
    },
  });
  const method = useWatch({ control, name: 'paymentMethod' });
  const previousMethod = useRef(method);
  useEffect(() => {
    // تغییر روش پرداخت طرف انتخاب‌شده را پاک می‌کند (صندوق، حساب بانکی یا تأمین‌کننده).
    if (previousMethod.current !== method) {
      previousMethod.current = method;
      setValue('counterpartyId', '');
    }
  }, [method, setValue]);
  const counterparties =
    method === 'Cash'
      ? (options.data?.cashRegisters ?? [])
      : method === 'Bank'
        ? (options.data?.bankAccounts ?? [])
        : (options.data?.suppliers ?? []);
  const save = useMutation({
    mutationFn: async (form: ExpenseForm) => {
      const document = file ? { fileName: file.name, contentBase64: await fileToBase64(file) } : null;
      return unwrap(
        tenantApi.POST('/api/v1/accounting/expenses', {
          body: expenseBody(form, document),
          headers: { 'Idempotency-Key': key.current() },
        }),
      );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['categoryId', 'amount', 'tax', 'businessDate', 'branchId'],
        aliases: {
          cashRegisterId: 'counterpartyId',
          bankAccountId: 'counterpartyId',
          supplierId: 'counterpartyId',
        },
        codeFields: { 'PHX-ACC-0403': 'businessDate' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    const tooLarge = chosen !== null && chosen.size > MAX_DOCUMENT_BYTES;
    setFileError(tooLarge);
    setFile(tooLarge ? null : chosen);
  };
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-884-FORM"
      titleKey="acc:expense.add"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      {options.isPending ? <LoadingState rows={3} /> : null}
      {options.error ? (
        <ErrorState error={options.error} compact onRetry={() => void options.refetch()} />
      ) : null}
      {options.data && options.data.categories.length === 0 ? (
        <EmptyState titleKey="acc:expense.no_categories" />
      ) : null}
      <Stack spacing={2} sx={{ mt: 1 }}>
        <SelectRHF
          control={control}
          name="categoryId"
          labelKey="acc:expense.category"
          required
          fullWidth
          options={(options.data?.categories ?? []).map((item) => ({
            value: item.value,
            label: item.label ?? undefined,
          }))}
        />
        <MoneyFieldRHF control={control} name="amount" labelKey="acc:expense.amount" required fullWidth />
        <MoneyFieldRHF control={control} name="tax" labelKey="acc:expense.tax" fullWidth />
        <JalaliDatePickerRHF control={control} name="businessDate" labelKey="acc:common.date" />
        <SelectRHF
          control={control}
          name="branchId"
          labelKey="acc:common.branch"
          required
          fullWidth
          options={branches.map((branch) => ({ value: branch.branchId, label: branch.name ?? undefined }))}
        />
        <SelectRHF
          control={control}
          name="paymentMethod"
          labelKey="acc:expense.method"
          fullWidth
          options={METHODS.map((value) => ({ value, labelKey: `acc:expense_method.${value}` }))}
        />
        <SelectRHF
          control={control}
          name="counterpartyId"
          labelKey={`acc:expense.counterparty_${method}`}
          fullWidth
          options={[
            ...(method === 'Cash' ? [{ value: '', labelKey: 'acc:expense.default_register' }] : []),
            ...counterparties.map((item) => ({ value: item.value, label: item.label ?? undefined })),
          ]}
        />
        <TextFieldRHF
          control={control}
          name="description"
          labelKey="acc:expense.description"
          fullWidth
          multiline
          minRows={2}
        />
        <Stack spacing={0.5}>
          <Typography component="label" htmlFor="acc-expense-file" variant="body2">
            {t('acc:expense.document')}
          </Typography>
          <input
            id="acc-expense-file"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={onFile}
            data-testid="acc-expense-file"
          />
          {fileError ? (
            <Typography variant="caption" color="error" role="alert">
              {t('acc:expense.document_too_large')}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('acc:expense.document_hint')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </FormDialog>
  );
}

const categorySchema = z.object({
  code: z.string().regex(/^[A-Za-z0-9_-]{1,30}$/, { error: 'acc:expense.code_invalid' }),
  title: z.string().trim().min(1, { error: 'common:validation.required' }).max(100),
  accountId: z.string(),
});
type CategoryForm = z.input<typeof categorySchema>;

/** دسته‌های هزینه با نگاشت حساب (acc.mapping.manage). */
function CategoriesDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [editing, setEditing] = useState<AccExpenseCategoryView | 'new' | null>(null);
  const [active, setActive] = useState(true);
  const [submitError, setSubmitError] = useState<unknown>();
  const categories = useQuery({
    queryKey: accKeys.expenseCategories(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/expense-categories')),
  });
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
  });
  const { control, handleSubmit, formState, reset, setError } = useForm<CategoryForm>({
    resolver: zodResolver(categorySchema),
    defaultValues: { code: '', title: '', accountId: '' },
  });
  const open = (row: AccExpenseCategoryView | 'new') => {
    setEditing(row);
    setActive(row === 'new' ? true : row.isActive);
    reset(
      row === 'new'
        ? { code: '', title: '', accountId: '' }
        : { code: row.code, title: row.title, accountId: '' },
    );
  };
  const save = useMutation({
    mutationFn: (form: CategoryForm) => {
      const body = {
        code: form.code.trim(),
        title: form.title.trim(),
        isActive: active,
        accountId: form.accountId || null,
      };
      return editing === 'new' || editing === null
        ? unwrap(
            tenantApi.POST('/api/v1/accounting/expense-categories', {
              body,
              headers: { 'Idempotency-Key': key.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/accounting/expense-categories/{id}', {
              params: { path: { id: editing.id } },
              body,
            }),
          );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
      setEditing(null);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'title', 'accountId'],
        codeFields: { 'PHX-ACC-0702': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  const postable = (accounts.data?.items ?? []).filter((account) => account.isActive);
  if (editing !== null) {
    return (
      <FormDialog
        open
        screenId="SCR-STAFF-884-CATEGORY"
        titleKey={editing === 'new' ? 'acc:expense.add_category' : 'acc:expense.edit_category'}
        onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
        onClose={() => setEditing(null)}
        isDirty={formState.isDirty}
        isSubmitting={save.isPending}
        submitError={submitError}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextFieldRHF
            control={control}
            name="code"
            labelKey="acc:common.code"
            required
            fullWidth
            disabled={editing !== 'new'}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'acc-category-code' } }}
          />
          <TextFieldRHF
            control={control}
            name="title"
            labelKey="acc:common.title"
            required
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': 'acc-category-title' } }}
          />
          <SelectRHF
            control={control}
            name="accountId"
            labelKey="acc:expense.account"
            fullWidth
            options={[
              { value: '', labelKey: 'acc:expense.keep_mapping' },
              ...postable.map((account) => ({
                value: account.id,
                label: `${account.code} — ${account.title}`,
              })),
            ]}
          />
          {editing !== 'new' ? (
            <FormControlLabel
              control={<Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />}
              label={t('acc:common.active')}
            />
          ) : null}
        </Stack>
      </FormDialog>
    );
  }
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-884-CATEGORIES"
      titleKey="acc:expense.categories"
      onSubmit={() => open('new')}
      submitKey="acc:expense.add_category"
      onClose={onClose}
      isDirty={false}
      isSubmitting={false}
    >
      {categories.isPending ? <LoadingState rows={3} /> : null}
      {categories.error ? (
        <ErrorState error={categories.error} compact onRetry={() => void categories.refetch()} />
      ) : null}
      {categories.data && categories.data.items.length === 0 ? (
        <EmptyState titleKey="acc:expense.categories_empty" />
      ) : null}
      <List aria-label={t('acc:expense.categories')}>
        {(categories.data?.items ?? []).map((row) => (
          <ListItem key={row.id} disablePadding>
            <ListItemButton onClick={() => open(row)} data-testid="acc-category-row" data-code={row.code}>
              <ListItemText
                primary={`${row.code} — ${row.title}`}
                secondary={
                  row.mappedAccountCode
                    ? t(row.usesDefaultMapping ? 'acc:expense.mapped_default' : 'acc:expense.mapped', {
                        code: row.mappedAccountCode,
                        title: row.mappedAccountTitle ?? '',
                      })
                    : t('acc:expense.unmapped')
                }
              />
              {!row.isActive ? <StatusChip tone="inactive" labelKey="acc:common.inactive" /> : null}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </FormDialog>
  );
}
